import { toOrigin } from '@bazak/analytics/security';

export const IS_PRODUCTION = process.env.NODE_ENV === 'production';
export const POSTHOG_ENABLED = IS_PRODUCTION || process.env.NEXT_PUBLIC_FORCE_ANALYTICS === 'true';

const RAW_DEPLOY_ENV = process.env.NEXT_PUBLIC_DEPLOY_ENV;
export const DEPLOY_ENV: 'development' | 'production' =
  RAW_DEPLOY_ENV === 'prod' || RAW_DEPLOY_ENV === 'production' ? 'production' : 'development';
export const IS_DEPLOY_PRODUCTION = DEPLOY_ENV === 'production';

export const POSTHOG_HOST = 'https://us.i.posthog.com';

/**
 * The origin `assetPrefix` points at, for the preconnect in the layout. Both halves of the
 * condition have to match `next.config.ts`, or the layout warms a connection to an origin nothing
 * is fetched from.
 */
export const STATIC_CDN_ORIGIN = IS_PRODUCTION
  ? toOrigin(process.env.NEXT_PUBLIC_STATIC_CDN_URL)
  : '';

export const BASE_URL = 'https://bazak.ai';

/**
 * A route's canonical, with the `llms.txt` alternate carried along.
 *
 * Next resolves `alternates` by replacement, not by merge, so a route that declares only
 * `{ canonical }` drops the root's `types` with it — and that alternate is an AEO surface this
 * site treats as a feature. Every route that sets a canonical goes through here.
 */
export function canonical(path: string) {
  // Both point at the same bytes. text/plain is the llms.txt convention; text/markdown is what a
  // client negotiates for, and naming it here means one can be found without guessing at the header.
  return {
    canonical: path,
    types: { 'text/plain': '/llms.txt', 'text/markdown': MARKDOWN_ROUTE },
  };
}
export const STATUS_PAGE_URL = 'https://status.bazak.ai';
export const PLATFORM_URL = 'https://os.bazak.ai';
// Same string today, but they name different things in structured data: the SoftwareApplication
// versus the Organization behind it. Keep them separate so a product rename cannot silently
// rename the company.
export const PRODUCT_NAME = 'Bazak';
export const COMPANY_NAME = 'Bazak';
export const SUPPORT_EMAIL = 'support@bazak.ai';
/** Where a commercial enquiry goes, as opposed to a support one. A role, not a person, so it
 *  survives someone changing jobs. */
export const SALES_EMAIL = 'sales@bazak.ai';

/**
 * The registered office, as it appears in the terms and the privacy notice. Structured data and
 * the legal pages have to say the same thing, so this is the one copy.
 */
export const COMPANY_ADDRESS = {
  street: 'Schiele 6',
  city: 'Tel Aviv – Jaffa',
  country: 'IL',
  countryName: 'Israel',
} as const;

/** The office as prose, for the legal pages. `COMPANY_ADDRESS` is the same fact in parts. */
export const COMPANY_ADDRESS_LINE = `${COMPANY_ADDRESS.street}, ${COMPANY_ADDRESS.city}`;
export const PRIVACY_EMAIL = 'privacy@bazak.ai';
export const HTTP_STATUS = { NOT_FOUND: 404 } as const;

export const DEMO_URL = 'https://calendar.app.google/mWmsY7uZ38ipdeXd7';

export const HUBSPOT_PORTAL_ID = '146410257';
// This portal lives in HubSpot's EU data center, which answers form submissions on its own
// host. The default api.hsforms.com returns 403 "Portal isn't allowed to post submissions".
export const HUBSPOT_FORMS_ORIGIN = 'https://api-eu1.hsforms.com';

export const LEGAL_LAST_UPDATED_ISO = '2026-05-08';
export const LEGAL_LAST_UPDATED_LABEL = 'May 8, 2026';

/**
 * Chrome origin-trial token for WebMCP. Without one `document.modelContext` is undefined for every
 * visitor, so the tool registers nowhere. Per-origin and expiring, hence config rather than a
 * constant — see `.env` for the expiry. Unset is a valid state: the feature is simply dormant.
 */
export const WEBMCP_ORIGIN_TRIAL_TOKEN = process.env.NEXT_PUBLIC_WEBMCP_ORIGIN_TRIAL ?? '';

/** The markdown representation of the site, and what `/` rewrites to when negotiated for. */
export const MARKDOWN_ROUTE = '/index.md';

export const LEGAL_ROUTES = {
  privacyNotice: '/privacy-notice',
  cookieNotice: '/cookie-notice',
  doNotSell: '/do-not-sell',
  termsOfUse: '/terms-of-use',
} as const;

export const MARKETING_ROUTES = {
  home: '/',
  scan: '/scan',
  industries: '/industries',
  /** Redirects to DEMO_URL — see app/demo/page.tsx for why the hop exists. */
  demo: '/demo',
  howItWorks: '/#how-it-works',
  industriesHash: '/#industries',
  platformHash: '/#platform',
  security: '/#security',
  news: '/#news',
  pricing: '/#pricing',
  book: '/#book',
  faq: '/#faq',
  scanner: '/#scanner',
} as const;

export const INDUSTRY_ROUTES = {
  travelHospitality: '/industries/travel-hospitality',
  retailEcommerce: '/industries/retail-ecommerce',
  financialServices: '/industries/financial-services',
  consumerGoods: '/industries/consumer-goods',
} as const;
